import { useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { transformFromStringToNumber } from 'components/modules/Insurance//Insurance.helpers';
import { useMultipleSelect } from 'components/modules/Insurance/hooks/useMultipleSelect';
import { useValidationParts } from 'components/modules/Insurance/hooks/useValidationParts';
import { getChangeEvent } from 'components/modules/Insurance/Insurance.helpers';
import { FIELDS } from 'store/insuranceAnketa/constants';

import { useDisabledFields } from '../../hooks/useDisabledFields';
import { useMaxDate } from '../../hooks/useMaxDate';
import { useStoreHelper } from '../../hooks/useStoreHelper';

import { DEFAULT_VALUES, NAMES, STORE_MAP, STORE_TRANSFORM } from './DriverLicense.constants';
import { generatePreviousLicense, generatePreviousLicenseParts } from './DriverLicense.helpers';
import { driverLicenseSchema } from './DriverLicense.validation';

export const useDriverLicensePresenter = ({ isOwnerForm }) => {
  const { errorSelector, errorsSelector, onChangeValue, valueSelector, valuesSelector } =
    useStoreHelper({ isOwnerForm });
  const { disabled: pageRestrict } = useDisabledFields();

  const { values: errors } = useMultipleSelect({ map: STORE_MAP, selector: errorsSelector });
  const { values } = useMultipleSelect({
    map: STORE_MAP,
    selector: valuesSelector,
    transform: STORE_TRANSFORM,
  });

  const previousLicenseError = useSelector(
    errorSelector(FIELDS.PREVIOUS_DRIVING_LICENSE_SERIES_AND_NUMBER)
  );
  const previousLicenseValue =
    useSelector(valueSelector(FIELDS.PREVIOUS_DRIVING_LICENSE_SERIES_AND_NUMBER)) ?? '';

  const previousLicensePartsValue = useMemo(() => {
    return generatePreviousLicenseParts({ previousLicense: previousLicenseValue });
  }, [previousLicenseValue]);

  const { errors: previousLicensePartsErrors } = useValidationParts({
    error: previousLicenseError,
    parts: previousLicensePartsValue,
    schema: driverLicenseSchema,
  });

  const { maxDate: maxPreviousDateIssue } = useMaxDate({ date: values[NAMES.DATE_ISSUE] });

  const onChange = useCallback(
    (event) => {
      const { key, value } = getChangeEvent(event);
      const storeKey = STORE_MAP[key];
      const params = [];

      switch (key) {
        case NAMES.CHANGED:
          params.push({ key: storeKey, value: !value });
          break;

        case NAMES.EXPERIENCE:
          params.push({ key: storeKey, value: value && transformFromStringToNumber(value) });
          break;

        case NAMES.PREVIOUS_NUMBER:
        case NAMES.PREVIOUS_SERIES:
          params.push({
            key: FIELDS.PREVIOUS_DRIVING_LICENSE_SERIES_AND_NUMBER,
            value: generatePreviousLicense({
              previousLicenseParts: previousLicensePartsValue,
              key,
              value,
            }),
          });
          break;

        default:
          params.push({ key: storeKey, value });
          break;
      }

      for (const param of params) {
        onChangeValue(param);
      }
    },
    [previousLicensePartsValue, onChangeValue]
  );

  return {
    disabled: pageRestrict,
    errors: { ...errors, ...previousLicensePartsErrors },
    maxPreviousDateIssue,
    onChange,
    values: { ...DEFAULT_VALUES, ...values, ...previousLicensePartsValue },
  };
};
